'use client'

import SearchResultCard from '../search-result-card'
import s from './style.module.css'
import {ReactNode, useEffect, useState} from 'react'
import SearchResultsShimmer from './shimmer'
import useSearchStore from '../store/search-store'
import {filterResults, SearchGame} from './helper'
import SearchSectionAnalyticsWrapper from '@app/components/header/analytics/visiblity-analytics-wrapper'
import {SearchEvent} from '@app/components/header/analytics/events'
import AnimateOnVisibleWrapper from '@app/components/sidebar/sidebar-tags-container/animate-visiblity/animate-visiblity-wrapper'
import {LanguageCodes} from '@app/i18n/settings'
import {useTagsPageAnalytics} from '@app/[lang]/(common-header-footer)/tags/(tag-page)/[tagName]/[tagId]/get-tag-page-analytics/tag-page-analytics-context'
import {ReffererLayout} from '@app/analytics/analytics-provider/types'

type Props = {
  featuredGamesInSearch: ReactNode
  propertyCode: string
  noResultsText: string
  allSearchGames: SearchGame[]
  lang: LanguageCodes
}

export const resultsLimit = 10

const SearchResultsClient = ({featuredGamesInSearch, propertyCode, noResultsText, allSearchGames, lang}: Props) => {
  const {searchQuery, searchResults, allGames, setAllGames, setSearchResults} = useSearchStore()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setAllGames(allSearchGames)
  }, [])

  useEffect(() => {
    if (searchQuery) {
      setLoading(true)
      const results = filterResults({
        allGames,
        text: searchQuery,
        lang,
      }).slice(0, resultsLimit)
      setSearchResults(results)
      setLoading(false)
    } else {
      setSearchResults([])
    }
  }, [searchQuery])

  const {tagAnalyticsData} = useTagsPageAnalytics()

  const additionalAttributes = {
    ...tagAnalyticsData,
    'input-fields': searchQuery,
    'result-count': searchResults.length,
  } as Record<string, string | number | boolean>

  return (
    <>
      {loading && <SearchResultsShimmer />}
      {!loading && searchResults.length === 0 && searchQuery && (
        <>
          <SearchSectionAnalyticsWrapper
            event={SearchEvent.NoResultsShown}
            additionalAttributes={additionalAttributes}
            className={s.no_results}
          >
            <p className={s.no_results_text}>{noResultsText}</p>
          </SearchSectionAnalyticsWrapper>
          {featuredGamesInSearch}
        </>
      )}

      {searchResults.length > 0 && (
        <SearchSectionAnalyticsWrapper
          event={SearchEvent.ResultsShown}
          additionalAttributes={additionalAttributes}
          className={s.featured_games_results}
        >
          {searchResults.map((game, index) => (
            <AnimateOnVisibleWrapper index={index} key={game.code}>
              <SearchResultCard
                key={game.code}
                gameName={game.name}
                gameNameEn={game.nameEn}
                gameCode={game.code}
                propertyCode={propertyCode}
                playCount={game.playCount}
                refferalLayout={'search-result'}
              />
            </AnimateOnVisibleWrapper>
          ))}
        </SearchSectionAnalyticsWrapper>
      )}
    </>
  )
}

export default SearchResultsClient
